import React, { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { ReactComponent as ElementsIcon } from "../../Assets/icon/Elements.svg";
import { ReactComponent as ConnectedCheckIcon } from "../../Assets/icon/status-connected-check.svg";
import { ReactComponent as StatusInfoIcon } from "../../Assets/icon/status-info-circle.svg";
import { addRelationship, uploadURL, editRelationship, userLookup } from "../../Services/Realtionship";
import { ReactComponent as MemberIcon } from "../../Assets/icon/green-user.svg"; // Aris Member
import { ReactComponent as NotMemberIcon } from "../../Assets/icon/UserCircleCross.svg"; // Not a member
import { ReactComponent as EyeIcon } from "../../Assets/images/eye.svg"; // Not a member
import "./style.css";
import ContentLoader from "../../Pages/LoadingPage/ContentLoader";
import useRelationshipOptions from "../RelationshipForm/useRelationshipOptions";

// Simple validators
const isValidEmail = (v) => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(String(v || "").trim());
const isValidPhone = (v) => /[0-9]{7,}/.test(String(v || "").replace(/\D/g, ""));

const initialForm = {
  first_name: "",
  last_name: "",
  relationship_category: "",
  relationship_type: "",
  relationship_status: "",
  relationship_length: "",
  when_met: "",
  // optional
  email: "",
  country_code: "+1",
  contact: "",
  profile_pic_url: "",
};

export default function AddRelationshipForm({
  onCancel,
  onSuccess,
  openConfirmInvite,
  onCheckMember,
  mode = "create", // 'create' | 'edit'
  initialValues = null,
  relationId = null,
  shareState = null, // 'accepted' | 'pending' | null
  shareContact = null,
  hideTitle = false,
  isSurveyAdd = false,
  emailOnlyMode = false,
}) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    try { mq.addEventListener('change', handler); } catch { mq.addListener(handler); }
    return () => { try { mq.removeEventListener('change', handler); } catch { mq.removeListener(handler); } };
  }, []);
  const hydrate = (vals) => {
    if (!vals) return initialForm;
    const next = { ...initialForm, ...vals };
    // Normalize phone input value to e164 for PhoneInput
    if (vals?.country_code && vals?.contact && !vals?.contact.startsWith("+")) {
      next.contact = `${vals.country_code}${vals.contact}`;
      next.country_code = vals.country_code;
      next.contact_local = vals.contact;
    }
    return next;
  };
  const [form, setForm] = useState(hydrate(initialValues));
  const [notifyVia, setNotifyVia] = useState(() => {
    if (initialValues?.contact?.email) return "email";
    if (initialValues?.contact?.mobile?.country_code && initialValues?.contact?.mobile?.number) return "phone";
    return "email";
  }); // 'email' | 'phone'
  const [checking, setChecking] = useState(false);
  const [memberStatus, setMemberStatus] = useState(null); // null | 'member' | 'not'
  const [memberInfo, setMemberInfo] = useState(null); // Store user info when member
  const [planError, setPlanError] = useState(null); // Store 403 plan validation error
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Always reset verification result when channel or value changes
  useEffect(() => {
    setMemberStatus(null);
    setMemberInfo(null);
    setPlanError(null);
  }, [notifyVia, form.email, form.contact]);

  // When parent updates initialValues (e.g., after async fetch), rehydrate the form
  useEffect(() => {
    const next = hydrate(initialValues);
    setForm(next);
    // Derive notifyVia from new values if present
    if (next?.email) {
      setNotifyVia("email");
    } else if (next?.country_code && next?.contact) {
      setNotifyVia("phone");
    }
  }, [initialValues]);

  // Options for category/type/status
  const { categories, types, statuses, loadTypes } = useRelationshipOptions({ initialCategory: form.relationship_category });
  useEffect(() => {
    if (form.relationship_category) {
      loadTypes(form.relationship_category);
    }
  }, [form.relationship_category, loadTypes]);

  // If we are in email-only mode (survey or license flows), force notifyVia to email
  useEffect(() => {
    if (emailOnlyMode || isSurveyAdd) {
      setNotifyVia("email");
    }
  }, [emailOnlyMode, isSurveyAdd]);

  // Removed auto-lookup in edit pending share: lookup should only occur on explicit Verify action

  const canVerify = useMemo(() => {
    if (notifyVia === "email") return isValidEmail(form.email);
    const parsed = form.contact || form.parsed_contact ? parsePhoneNumberFromString(form.contact || form.parsed_contact) : null;
    return Boolean(parsed && parsed.isValid());
  }, [notifyVia, form.email, form.contact, form.parsed_contact]);

  const handleChange = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((p) => ({
      ...p,
      [key]: value,
      ...(key === "relationship_category" ? { relationship_type: "" } : {}),
    }));
    if (key === "email" || key === "contact") {
      setMemberStatus(null);
    }
    if (key === "email" && value) {
      setNotifyVia("email");
    }
  };

  const handlePhoneChange = (value) => {
    const e164 = value || "";
    const parsed = e164 ? parsePhoneNumberFromString(e164) : null;
    const cc = parsed ? `+${parsed.countryCallingCode}` : form.country_code || "+1";
    const local = parsed ? parsed.nationalNumber : "";
    setForm((p) => ({ ...p, contact: e164, country_code: cc, contact_local: local }));
    setMemberStatus(null);
    // Ensure the channel reflects user's current action
    if (e164) {
      setNotifyVia("phone");
    }
  };

  const handleVerify = async () => {
    if (!canVerify) return;
    setChecking(true);
    setPlanError(null);
    try {
      let result = {};
      // Prefer backend lookup endpoint
      if (notifyVia === "email") {
        result = await userLookup({ email: form.email?.trim() });
      } else {
        // Ensure we pass parsed parts
        const parsed = form.contact ? parsePhoneNumberFromString(form.contact) : null;
        const cc = parsed ? `+${parsed.countryCallingCode}` : form.country_code;
        const local = parsed ? parsed.nationalNumber : form.contact_local;
        result = await userLookup({ country_code: cc, contact: local });
      }

      // Handle 403 plan validation error
      if (result.error && result.error.status === 403) {
        setPlanError(result.error.message);
        setMemberStatus(null);
        setMemberInfo(null);
        return;
      }

      setMemberStatus(result.exists ? "member" : "not");

      // If user is a member, pre-populate their information
      if (result.exists && result.user) {
        const userInfo = result.user;
        setMemberInfo(userInfo);
        setForm((prev) => ({
          ...prev,
          first_name: userInfo.first_name || prev.first_name,
          last_name: userInfo.last_name || prev.last_name,
          profile_pic_url: userInfo.profile_pic_url || prev.profile_pic_url,
        }));
      } else {
        setMemberInfo(null);
        // Clear form fields if not a member
        setForm((prev) => ({
          ...prev,
          first_name: "",
          last_name: "",
          profile_pic_url: "",
        }));
      }
    } finally {
      setChecking(false);
    }
  };

  // --- Modular helpers to simplify handleSubmit ---
  const buildPayload = () => {
    const base = {
      first_name: form.first_name,
      last_name: form.last_name,
      relationship_type: form.relationship_type,
      relationship_length: form.relationship_length,
      relationship_category: form.relationship_category,
      relationship_status: form.relationship_status,
      flow: isSurveyAdd ? "survey" : "invite",
    };
    if (mode === "edit") {
      if (notifyVia === "email") {
        return { ...base, email: form.email || "", country_code: "", contact: "" };
      }
      return { ...base, email: "", country_code: form.country_code || "", contact: form.contact_local ? String(form.contact_local) : "" };
    }
    return {
      ...base,
      email: notifyVia === "email" && form.email ? form.email : undefined,
      country_code: notifyVia === "phone" && form.country_code ? form.country_code : undefined,
      contact: notifyVia === "phone" && form.contact_local ? String(form.contact_local) : undefined,
    };
  };

  const extractRelationId = (res) =>
    res?.payload?._id || res?.payload?.relation_id || res?.data?._id || res?._id || res?.payload?.data?._id;

  const uploadProfilePhoto = async (targetRelationId, contextLabel = "") => {
    if (!photo || !targetRelationId) return;
    try {
      const upReq = { image: photo.name, image_type: (photo.type || "image/jpeg").split("/")[1] };
      const upRes = await uploadURL(targetRelationId, upReq);
      const imagePayload = upRes?.payload?.find((p) => p.upload_url && (p.s3_key || p.image_key));
      if (imagePayload) {
        await fetch(imagePayload.upload_url, {
          method: "PUT",
          headers: { "Content-Type": photo.type || "image/jpeg" },
          body: photo,
        });
        const key = imagePayload.s3_key || imagePayload.image_key;
        if (key) {
          await editRelationship(targetRelationId, { profile_pic_url: key });
        }
      }
    } catch (e) {
      console.warn(`Optional photo upload failed${contextLabel ? ` (${contextLabel})` : ""}:`, e);
    }
  };

  const editWithOptionalPhoto = async (relationIdArg, payloadArg, contextLabel = "edit path") => {
    const res = await editRelationship(relationIdArg, payloadArg);
    await uploadProfilePhoto(relationIdArg, contextLabel);
    return res;
  };

  const createWithOptionalPhoto = async (payloadArg, contextLabel = "create path") => {
    const res = await addRelationship(payloadArg);
    const rid = extractRelationId(res);
    await uploadProfilePhoto(rid, contextLabel);
    return res;
  };

  const handleEditInviteFlow = async (payloadArg) => {
    if (notifyVia === "phone") {
      // Direct invite without confirmation in edit mode (phone)
      setSubmitting(true);
      try {
        await editWithOptionalPhoto(relationId, payloadArg, "edit invite path");
        onSuccess?.();
      } finally {
        setSubmitting(false);
      }
    } else {
      // Email path: ask for confirmation, then invite via edit
      openConfirmInvite?.({
        form,
        notifyVia,
        photo,
        onConfirm: async () => {
          setSubmitting(true);
          try {
            const res = await editWithOptionalPhoto(relationId, payloadArg, "edit invite path");
            onSuccess?.(res);
          } finally {
            setSubmitting(false);
          }
        },
      });
    }
  };

  const handleCreateInviteFlow = async (payloadArg) => {
    if (notifyVia === "phone") {
      // Direct invite without confirmation in create mode (phone)
      setSubmitting(true);
      try {
        const res = await createWithOptionalPhoto(payloadArg, "invite path");
        onSuccess?.(res);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Email path: confirmation first
      openConfirmInvite?.({
        form,
        notifyVia,
        photo,
        onConfirm: async () => {
          setSubmitting(true);
          try {
            const res = await createWithOptionalPhoto(payloadArg, "invite path");
            onSuccess?.(res);
          } finally {
            setSubmitting(false);
          }
        },
      });
    }
  };

  const submitEditNormal = async (payloadArg) => {
    setSubmitting(true);
    try {
      const res = await editWithOptionalPhoto(relationId, payloadArg, "edit normal path");
      onSuccess?.(res);
    } finally {
      setSubmitting(false);
    }
  };

  const submitCreateNormal = async (payloadArg) => {
    setSubmitting(true);
    try {
      const res = await createWithOptionalPhoto(payloadArg);
      onSuccess?.(res);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    const payload = buildPayload();
    const hasContact = (notifyVia === "email" && form.email) || (notifyVia === "phone" && form.contact);

    if (mode === "edit" && relationId) {
      if (hasContact && memberStatus === "not") {
        await handleEditInviteFlow(payload);
        return;
      }
      await submitEditNormal(payload);
      return;
    }

    // Create mode - check if verification was done
    if (memberStatus === null) {
      // Should not happen due to button disabled state, but just in case
      return;
    }

    // If member is not an Aris member, show confirmation dialog
    if (hasContact && memberStatus === "not") {
      await handleCreateInviteFlow(payload);
      return;
    }

    // For Aris members, directly create the relationship
    await submitCreateNormal(payload);
  };

  const hasVerifyInputValue =
    notifyVia === "email"
      ? String(form.email || "").trim().length > 0
      : Boolean(form.contact);

  const memberBadge = (
    <div
      className={`verify-badge ${memberStatus === "member" ? "ok" : memberStatus === "not" ? "not" : "idle"
        } ${hasVerifyInputValue ? "member-verify-active" : ""}`}
    >
      {memberStatus === "member" ? (
        <><MemberIcon /> <span>Aris member — continue to add your connection</span></>
      ) : memberStatus === "not" ? (
        <><NotMemberIcon /> <span className="not-member">Not an Aris member — invite them to join Aris</span></>
      ) : (
        <><EyeIcon /> <span>Aris Member? Verify</span></>
      )}
    </div>
  );

  const requiredComplete = useMemo(() => {
    const base = (
      String(form.first_name || "").trim().length > 0 &&
      String(form.last_name || "").trim().length > 0 &&
      String(form.relationship_category || "").trim().length > 0 &&
      String(form.relationship_type || "").trim().length > 0 &&
      String(form.relationship_status || "").trim().length > 0
    );
    if (emailOnlyMode && isSurveyAdd) {
      return base && isValidEmail(form.email);
    }
    return base;
  }, [form.first_name, form.last_name, form.relationship_category, form.relationship_type, form.relationship_status, form.email, emailOnlyMode, isSurveyAdd]);

  return (
    <div className="addrel-card">
      {!hideTitle && (
        <div className="addrel-header addrel-title-header">
          <h3 className="addrel-desktop-title">{mode === 'edit' ? 'Edit Connection' : 'Add Connection'}</h3>
          {mode !== 'edit' && <div className="verify-sub">Request per mission to mutually share insights for deeper relationship understanding.</div>}
        </div>
      )}

      {/* Verification Section - Always show first for create mode */}
      {mode === "create" && !(emailOnlyMode || isSurveyAdd) && (
        <div className="verification-section">
          <div className="verify-header">
            <div className="verify-instruction">Verify if your connection is an Aris member.</div>
          </div>
          <div className="verify-content">
            <div className="notify-row">
              <div className="notify-label">
                <span></span>
              </div>
              <div className="notify-radio">
                <label><input type="radio" checked={notifyVia === "email"} onChange={() => { setNotifyVia("email"); setMemberStatus(null); setMemberInfo(null); }} /> Email</label>
                <label><input type="radio" checked={notifyVia === "phone"} onChange={() => { setNotifyVia("phone"); setMemberStatus(null); setMemberInfo(null); }} /> Phone Number</label>
              </div>
            </div>

            {notifyVia === "email" ? (
              <div className="field">
                <div className="verify-add-form-field">
                  <input
                    className="verify-add-form-input"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="Email"
                  />
                  <button
                    className="verify-btn"
                    disabled={!canVerify || checking}
                    onClick={handleVerify}
                    type="button"
                  >
                    {checking ? "Checking..." : <><EyeIcon /> Verify</>}
                  </button>
                </div>
                {planError && (
                  <div className="verify-result">
                    <div className="verify-badge error-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 20 20" fill="none">
                        <path d="M13.3366 9.64367L17.2135 5.76678C17.3381 5.64304 17.4384 5.49734 17.5087 5.33807C17.5791 5.17881 17.6179 5.00913 17.6232 4.83883C17.6284 4.66852 17.5998 4.50097 17.5391 4.34583C17.4784 4.19069 17.3868 4.05103 17.2696 3.93491L15.71 2.40937C15.4756 2.17587 15.1524 2.05071 14.8109 2.06131C14.4695 2.07192 14.1378 2.21742 13.8886 2.46594L10.0147 6.29497L6.13782 2.41806C6.01409 2.29348 5.86838 2.19314 5.70912 2.12283C5.54985 2.05252 5.38017 2.01363 5.20987 2.00842C5.03957 2.0032 4.87201 2.03176 4.71687 2.09244C4.56173 2.15313 4.42207 2.24473 4.30595 2.36198L2.78042 3.9216C2.54691 4.15593 2.42175 4.47921 2.43236 4.82062C2.44296 5.16203 2.58846 5.49372 2.83699 5.74302L6.66604 9.61685L2.78916 13.4937C2.66457 13.6175 2.56423 13.7632 2.49392 13.9224C2.42361 14.0817 2.38472 14.2514 2.37951 14.4217C2.3743 14.592 2.40286 14.7595 2.46354 14.9147C2.52422 15.0698 2.61583 15.2095 2.73307 15.3256L4.29269 16.8511C4.52702 17.0846 4.8503 17.2098 5.19171 17.1992C5.53312 17.1886 5.86481 17.0431 6.11411 16.7946L9.98792 12.9655L13.8648 16.8424C13.9885 16.967 14.1342 17.0673 14.2935 17.1376C14.4527 17.208 14.6224 17.2468 14.7927 17.2521C14.963 17.2573 15.1306 17.2287 15.2857 17.168C15.4409 17.1073 15.5805 17.0157 15.6966 16.8985L17.2222 15.3389C17.4557 15.1045 17.5808 14.7813 17.5702 14.4399C17.5596 14.0984 17.4141 13.7668 17.1656 13.5175L13.3366 9.64367Z" fill="#F04438" />
                      </svg>
                      <span className="error-text">{planError}</span>
                    </div>
                  </div>
                )}
                {!planError && memberStatus !== null && (
                  <div className="verify-result">{memberBadge}</div>
                )}
              </div>
            ) : (
              <div className="field">
                <div className="verify-add-form-field">
                  <PhoneInput
                    international
                    defaultCountry="US"
                    value={form.contact}
                    onChange={handlePhoneChange}
                    placeholder="Mobile Number"
                    className="phone-input"
                  />
                  <button
                    className="verify-btn"
                    disabled={!canVerify || checking}
                    onClick={handleVerify}
                    type="button"
                  >
                    {checking ? "Checking..." : <><EyeIcon /> Verify</>}
                  </button>
                </div>
                {planError && (
                  <div className="verify-result">
                    <div className="verify-badge error-badge">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="#DC2626" />
                      </svg>
                      <span className="error-text">{planError}</span>
                    </div>
                  </div>
                )}
                {!planError && memberStatus !== null && (
                  <div className="verify-result">{memberBadge}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* In edit mode with accepted/pending share state, show status section at the top */}
      {mode === "edit" && !(emailOnlyMode || isSurveyAdd) && (shareState === "accepted" || shareState === "pending") && (
        <div className={`share-inline ${shareState === "accepted" ? "accepted" : "pending"}`}>
          <div className="share-inline-contact-info">
            <div className="share-inline-label">
              {/* Determine label based on contact type */}
              {(shareContact || form.email || "").includes("@") ? "Email:" : "Phone Number:"}
            </div>
            <div className="share-inline-contact">{shareContact || form.email || form.contact}</div>
          </div>
          <div className={`share-inline-status ${shareState === "pending" ? "share-inline-status-pending" : ""}`}>
            {shareState === "accepted" ? (
              <><ConnectedCheckIcon style={{ width: 18, height: 18 }} /> <span>Connected</span></>
            ) : (
              <><StatusInfoIcon style={{ width: 15, height: 15 }} /> <span>Approval Pending</span></>
            )}
          </div>
        </div>
      )}

      {/* Show details section only after verification or for edit mode, but not if there's a plan error */}
      {!planError && (mode === "edit" || memberStatus !== null || (emailOnlyMode && isSurveyAdd)) && (
        <>
          <div className="addrel-header">
            <label className="avatar-label">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setPhoto(f);
                }}
                style={{ display: "none" }}
              />
              <div className="avatar-placeholder">
                {photo ? (
                  <img src={URL.createObjectURL(photo)} alt="preview" className="avatar-img" />
                ) : (
                  form?.profile_pic_url && String(form.profile_pic_url).startsWith("http") ? (
                    <img src={form.profile_pic_url} alt="profile" className="avatar-img" />
                  ) : null
                )}
              </div>
              <span className="upload-text link">Upload Photo</span>
            </label>
          </div>

          <div className="grid-2">
            <div className="field">
              <label>First Name</label>
              <input value={form.first_name} onChange={handleChange("first_name")} placeholder="First Name" />
            </div>
            <div className="field">
              <label>Last Name</label>
              <input value={form.last_name} onChange={handleChange("last_name")} placeholder="Last Name" />
            </div>
            <div className="field">
              <label>Relationship Category</label>
              <select value={form.relationship_category} onChange={handleChange("relationship_category")}>
                <option value="">Select Category</option>
                {Array.isArray(categories) && categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Relationship Type</label>
              <select value={form.relationship_type} onChange={handleChange("relationship_type")}>
                <option value="">Select Type</option>
                {Array.isArray(types) && types.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Relationship Status</label>
              <select value={form.relationship_status} onChange={handleChange("relationship_status")}>
                <option value="">Select Status</option>
                {Array.isArray(statuses) && statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Email-only mode: show a simple email field and hide share section */}
          {(emailOnlyMode && isSurveyAdd) && (
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                placeholder="Email"
              />
            </div>
          )}

        </>
      )}

      {!planError && (mode === "edit" || memberStatus !== null) && <div className="actions">
        <button
          className="primary"
          onClick={handleSubmit}
          disabled={!requiredComplete || (mode === "create" && memberStatus === null)}
        >
          {mode === "edit" ? "Update" : (memberStatus === "member" ? "Connect" : memberStatus === "not" ? "Send Invite" : "Add Relationship")}
        </button>
        {!isSurveyAdd && <button className="ghost" onClick={onCancel}>Cancel</button>}
      </div>}
      {submitting && (
        <div className="addrel-submit-overlay" aria-live="polite" aria-busy="true">
          <ContentLoader />
        </div>
      )}
    </div>
  );
}
